import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks";
import { TherapistReviApi } from "../../api/revi/therapistApi";
import { message } from "antd";

const api = new TherapistReviApi();

export function useTherapistController() {
  // Los avisos de este hook los ve la institucion: son interfaz.
  const { t } = useTranslation("misc");
  const { user, accessToken } = useAuth();

  const [therapists, setTherapists] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingTherapist, setEditingTherapist] = useState(null);

  // Obtener ID de la institución (Identity Shield)
  const getInstitutionId = () => {
    const userData = user?.user || user;
    if (userData?.institutions?.length > 0) {
      return typeof userData.institutions[0] === "object"
        ? userData.institutions[0]._id
        : userData.institutions[0];
    }
    return null;
  };

  const institutionId = getInstitutionId();

  const loadTherapists = useCallback(
    async (page = 1) => {
      if (!institutionId) return;
      try {
        setLoading(true);
        const response = await api.getByInstitution(
          accessToken,
          institutionId,
          { page, limit: 12 },
        );
        setTherapists(response.therapists || []);
        setPagination({
          page: response.pagination.page,
          totalPages: response.pagination.totalPages,
          totalDocs: response.pagination.totalDocs,
        });
      } catch (error) {
        console.error(error);
        message.error(t("therapists.loadError"));
      } finally {
        setLoading(false);
      }
    },
    // `t` entra en las dependencias porque react-i18next lo cambia al cambiar
    // de idioma; su identidad es estable el resto del tiempo, asi que no
    // provoca recargas de mas.
    [accessToken, institutionId, t],
  );

  // --- NUEVA FUNCIÓN ---
  const handleUnlinkPatient = async (therapistId, patientId) => {
    try {
      const response = await api.unlinkPatient(
        accessToken,
        therapistId,
        patientId,
      );
      message.success(t("therapists.patientUnlinked"));

      // 1. Actualizamos la lista general en segundo plano
      setTherapists((prev) =>
        prev.map((t) => {
          if (t._id === therapistId) {
            // Reemplazamos el terapeuta viejo por el que devuelve el backend (ya sin el paciente)
            return response.therapist;
          }
          return t;
        }),
      );

      // 2. Devolvemos el terapeuta actualizado para que el Modal se entere
      return response.therapist;
    } catch (error) {
      console.error(error);
      message.error(t("therapists.unlinkError"));
      return null;
    }
  };

  useEffect(() => {
    loadTherapists();
  }, [loadTherapists]);

  // Handlers
  const openNewModal = () => {
    setEditingTherapist(null);
    setShowModal(true);
  };

  const openEditModal = (therapist) => {
    setEditingTherapist(therapist);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingTherapist(null);
  };

  const handleDelete = async (therapist) => {
    try {
      await api.delete(accessToken, therapist._id);
      message.success(t("therapists.deleted", { nombre: therapist.firstname }));
      loadTherapists(pagination.page);
    } catch (error) {
      message.error(error.message);
    }
  };

  return {
    therapists,
    pagination,
    loading,
    showModal,
    editingTherapist,
    institutionId, // Exponemos para el form
    loadTherapists,
    openNewModal,
    openEditModal,
    closeModal,
    handleDelete,
    handleUnlinkPatient,
  };
}
